/**
 * Recommendations page - View and manage trade recommendations.
 */

import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Visibility,
} from '@mui/icons-material';
import { recommendationsApi } from '../services/api';
import type { Recommendation } from '../types';
import { usePortfolio } from '../contexts/PortfolioContext';

export function RecommendationsPage() {
  const { selectedPortfolioId, selectedPortfolio } = usePortfolio();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRec, setSelectedRec] = useState<Recommendation | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<Recommendation['status']>('PENDING');
  const [userNotes, setUserNotes] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    if (selectedPortfolioId) {
      loadRecommendations();
    }
  }, [selectedPortfolioId]);

  const loadRecommendations = async () => {
    if (!selectedPortfolioId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await recommendationsApi.getOpen(selectedPortfolioId);
      setRecommendations(data.recommendations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recommendations');
      console.error('Recommendations load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedRec) return;

    try {
      await recommendationsApi.updateStatus(selectedRec.recommendationId, newStatus, userNotes);
      setStatusDialogOpen(false);
      setSelectedRec(null);
      setUserNotes('');
      await loadRecommendations();
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Failed to update recommendation status');
    }
  };

  const openStatusDialog = (rec: Recommendation) => {
    setSelectedRec(rec);
    setNewStatus(rec.status);
    setUserNotes(rec.userNotes || '');
    setStatusDialogOpen(true);
  };

  const filteredRecommendations = recommendations.filter((rec) => {
    if (statusFilter === 'all') return true;
    return rec.status === statusFilter;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const getActionTypeColor = (actionType: Recommendation['actionType']) => {
    switch (actionType) {
      case 'TRIM_POSITION':
        return 'warning';
      case 'INCREASE_POSITION':
        return 'success';
      case 'HOLD':
        return 'default';
      case 'REVIEW':
        return 'info';
      case 'DO_NOT_ADD':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: Recommendation['status']) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'ACKNOWLEDGED':
        return 'info';
      case 'EXECUTED':
        return 'success';
      case 'DISMISSED':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      {selectedPortfolio && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {selectedPortfolio.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedPortfolio.portfolioType} Portfolio
            </Typography>
          </CardContent>
        </Card>
      )}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">Trade Recommendations</Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                label="Filter by Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="ACKNOWLEDGED">Acknowledged</MenuItem>
                <MenuItem value="EXECUTED">Executed</MenuItem>
                <MenuItem value="DISMISSED">Dismissed</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Chip
            label={`${filteredRecommendations.length} recommendation(s)`}
            color="primary"
            variant="outlined"
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {filteredRecommendations.length === 0 ? (
            <Alert severity="info">No recommendations found</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Ticker</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Suggested</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRecommendations.map((rec) => (
                    <TableRow key={rec.recommendationId}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {rec.ticker}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={rec.actionType}
                          size="small"
                          color={getActionTypeColor(rec.actionType) as any}
                        />
                      </TableCell>
                      <TableCell>
                        {rec.suggestedShares
                          ? `${rec.suggestedShares} shares`
                          : rec.suggestedNotional
                          ? `$${rec.suggestedNotional.toFixed(2)}`
                          : 'N/A'}
                      </TableCell>
                      <TableCell>{rec.reasonCode}</TableCell>
                      <TableCell>
                        <Chip
                          label={rec.status}
                          size="small"
                          color={getStatusColor(rec.status) as any}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(rec.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Visibility />}
                            onClick={() => openStatusDialog(rec)}
                          >
                            Update
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Recommendation Status</DialogTitle>
        <DialogContent>
          {selectedRec && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Ticker: {selectedRec.ticker}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Action: {selectedRec.actionType}
              </Typography>
              <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                <InputLabel>New Status</InputLabel>
                <Select
                  value={newStatus}
                  label="New Status"
                  onChange={(e) => setNewStatus(e.target.value as Recommendation['status'])}
                >
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="ACKNOWLEDGED">Acknowledged</MenuItem>
                  <MenuItem value="EXECUTED">Executed</MenuItem>
                  <MenuItem value="DISMISSED">Dismissed</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes"
                value={userNotes}
                onChange={(e) => setUserNotes(e.target.value)}
                placeholder="Add any notes about this recommendation..."
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusUpdate} variant="contained">
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

